import { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { AuraInitParams, AuraMessage } from './auraUtils/aura.types';
import { HttpClient } from '../helpers';
import { BackendService } from '@/types/BackendService';
import { uniqueId } from 'react-bootstrap-typeahead/types/utils';
import useDevicesStore from '@/store/useDevicesStore';

// Add interface for context type
interface AuraContextType {
	open: boolean;
	toggleOpen: () => void;
	initialize: (params: AuraInitParams) => void;

	// Messages
	messages: AuraMessage[];
	pushMessage: (messages: AuraMessage) => void;

	newMessage: string;
	setNewMessage: (message: string) => void;
	sendNewMessage: () => Promise<any>;
}

const AuraContext = createContext<AuraContextType | undefined>(undefined);

export function useAuraContext() {
	const context = useContext(AuraContext);
	if (context === undefined) {
		throw new Error('useAuraContext must be used within an AuraProvider');
	}
	return context;
}

export function AuraProvider({ children }: { children: ReactNode }) {
	const [open, setOpen] = useState(false);
	const [currentUserFocus, setCurrentUserFocus] = useState<string | undefined>(undefined);

	const [messages, setMessages] = useState<AuraMessage[]>([]);

	const [newMessage, setNewMessage] = useState<string>('');

	const { devices } = useDevicesStore();

	const initialize = (params: AuraInitParams) => {
		setCurrentUserFocus(params.currentUserFocus);
	};

	const toggleOpen = () => {
		setOpen(!open);
	};

	useEffect(() => {
		if (currentUserFocus === 'alertsDashboard' && devices) {
			getInitialMessage();
		}
	}, [currentUserFocus, devices]);

	const pushMessage = (message: AuraMessage) => {
		setMessages((msgs) => [...msgs, message]);
	};

	const getInitialMessage = async () => {
		const initialMessage = getInitialMessageFromLocalStorage();

		if (
			initialMessage.initialMessageExpiration &&
			Date.now() < parseInt(initialMessage.initialMessageExpiration)
		) {
			console.log(initialMessage);
			const response = JSON.parse(initialMessage.initialMessage);

			const newMessage: AuraMessage = {
				id: response?.id || uniqueId(),
				message:
					response?.content ||
					'Ups! Parece que no hemos podido obtener el mensaje inicial.',
				type: 'aura',
				date: response?.date || new Date(),
			};

			pushMessage(newMessage);

			return;
		}

		const { data: response } = await HttpClient(BackendService.Realtime).post(
			'/aura/initial-message',
			{},
			{
				params: {
					device: devices?.uid || '',
				},
			}
		);
		console.log(response);
		// Check if the message is expired
		setInitialMessageInLocalStorage(response);

		const newMessage: AuraMessage = {
			id: response?.id || uniqueId(),
			message:
				response?.content || 'Ups! Parece que no hemos podido obtener el mensaje inicial.',
			type: 'aura',
			date: response?.date || new Date(),
		};

		pushMessage(newMessage);
	};

	const setInitialMessageInLocalStorage = (data: any) => {
		localStorage.setItem('aura-initial-message', JSON.stringify(data));
		localStorage.setItem(
			'aura-initial-message-expiration',
			(Date.now() + 24 * 60 * 60 * 1000).toString()
		);
	};

	const getInitialMessageFromLocalStorage = (): {
		initialMessage: any;
		initialMessageExpiration: any;
	} => {
		const initialMessage = localStorage.getItem('aura-initial-message');
		const initialMessageExpiration = localStorage.getItem('aura-initial-message-expiration');

		return {
			initialMessage,
			initialMessageExpiration,
		};
	};

	const sendNewMessage = async () => {
		const mapMsgs = messages.map((msg) => {
			return {
				role: msg.type === 'aura' ? 'assistant' : 'user',
				content: msg.message,
			};
		});

		const initialMessage = JSON.parse(getInitialMessageFromLocalStorage().initialMessage);

		console.log(initialMessage.sessionId);

		const { data: response } = await HttpClient(BackendService.Realtime).post(
			'/aura/initial-message',
			{
				messageHistory: [...mapMsgs, { role: 'user', content: newMessage }],
				sessionId: initialMessage?.sessionId,
				ask: newMessage,
			},
			{
				params: {
					device: devices?.uid || '',
				},
			}
		);

		pushMessage({
			id: response?.id || uniqueId(),
			message: response?.content || 'Ups! Parece que no hemos podido obtener el mensaje.',
			type: 'aura',
			date: response?.date || new Date(),
		});

		setNewMessage('');

		return response;
	};

	return (
		<AuraContext.Provider
			value={{
				open,
				toggleOpen,
				initialize,
				messages,
				pushMessage,
				newMessage,
				setNewMessage,
				sendNewMessage,
			}}
		>
			{children}
		</AuraContext.Provider>
	);
}
